import { useState, useEffect, useRef, useCallback } from 'react'

interface AudioInputDevice {
  deviceId: string
  label: string
}

interface UseAudioInputReturn {
  devices: AudioInputDevice[]
  selectedDeviceId: string | null
  selectDevice: (deviceId: string) => void
  level: number // 0-1
  isMonitoring: boolean
  error: string | null
  /** Re-runs getUserMedia from a user gesture, which Safari sometimes requires. */
  retry: () => void
}

export function useAudioInput(): UseAudioInputReturn {
  const [devices, setDevices] = useState<AudioInputDevice[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [level, setLevel] = useState(0)
  const [isMonitoring, setIsMonitoring] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number>(0)

  const stopMonitoring = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
    setLevel(0)
    setIsMonitoring(false)
  }, [])

  const startMonitoring = useCallback(async (deviceId?: string) => {
    stopMonitoring()

    try {
      const constraints: MediaStreamConstraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream

      // Safari only exposes the prefixed constructor on older builds, and — the
      // reason the meter went dead there — it starts every context suspended
      // until a user gesture. A suspended analyser reports pure silence, so the
      // bar sat at zero however loudly the kid read.
      const Ctor: typeof AudioContext =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const audioCtx = new Ctor()
      audioCtxRef.current = audioCtx
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume().catch(() => {})
      }
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.5
      source.connect(analyser)
      analyserRef.current = analyser
      setIsMonitoring(true)

      const dataArray = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        if (!analyserRef.current) return
        // Safari re-suspends when the tab loses focus and does not always
        // resume itself; nudge it rather than silently flatlining.
        const ctx = audioCtxRef.current
        if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {})
        analyserRef.current.getByteFrequencyData(dataArray)
        let sum = 0
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i]
        const avg = sum / dataArray.length / 255
        setLevel(avg)
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)

      const deviceList = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = deviceList
        .filter((d) => d.kind === 'audioinput' && d.deviceId)
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${i + 1}`,
        }))
      setDevices(audioInputs)

      if (!deviceId && audioInputs.length > 0) {
        const activeTrack = stream.getAudioTracks()[0]
        const activeSettings = activeTrack?.getSettings()
        setSelectedDeviceId(activeSettings?.deviceId ?? audioInputs[0].deviceId)
      }
    } catch (e: any) {
      if (e.name === 'NotAllowedError') {
        setError('Microphone access denied')
      } else if (e.name === 'NotFoundError') {
        setError('No microphone found')
      } else {
        setError('Could not access microphone')
      }
    }
  }, [stopMonitoring])

  const selectDevice = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId)
    startMonitoring(deviceId)
  }, [startMonitoring])

  useEffect(() => {
    startMonitoring()
    return stopMonitoring
  }, [])

  // If the context was still blocked at mount, the first tap or key unblocks it.
  useEffect(() => {
    const unblock = () => {
      const ctx = audioCtxRef.current
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {})
    }
    const events = ['pointerdown', 'keydown', 'touchstart'] as const
    events.forEach((e) => window.addEventListener(e, unblock, { passive: true }))
    return () => events.forEach((e) => window.removeEventListener(e, unblock))
  }, [])

  const retry = useCallback(() => {
    setError(null)
    startMonitoring(selectedDeviceId ?? undefined)
  }, [startMonitoring, selectedDeviceId])

  return { devices, selectedDeviceId, selectDevice, level, isMonitoring, error, retry }
}
